import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { CopyIcon, KeyRoundIcon, PlusIcon } from 'lucide-react';

import {
  ApiError, api,
  type ApiKeyRow, type ConsumerRow, type IssuedKey, type Scope,
} from '@/lib/api';
import { useDateTime, useT } from '@/lib/i18n';
import type { StringKey } from '@/lib/strings/en';
import { useCannotBecause } from '@/lib/permissions';
import { notify } from '@/lib/notify';
import { DataTable } from '@/components/data-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from '@/components/ui/dialog';

/**
 * Settings → API keys (`plan-preproduction` X3).
 *
 * Replaces a disabled button and a header-only table that had been standing in
 * front of two live endpoints since Workstream G2. `POST /api/consumers` and
 * `POST /api/consumers/:id/keys` both worked; the only way to reach them was
 * curl, and the screen said issuance "lands with the integration phase" — a
 * sentence about a future that had already arrived.
 *
 * ### The plaintext is shown ONCE, and this screen is the only place it exists
 *
 * The server stores a hash. There is no read route that returns a key, no
 * support path that can recover one, and no second render of this dialog that
 * would show it again — closing the dialog destroys the only copy the console
 * ever held. Everything about the issue and rotate flows is shaped by that:
 * the notice is in the dialog rather than in a toast that scrolls away, the
 * value sits in a `readOnly` input so it can be selected as well as copied, and
 * "Done" is the only way out, so nobody dismisses it by clicking beside it and
 * discovers the loss later. The precedent is `CreateUserDialog`'s generated
 * password, and it is deliberately the same shape.
 *
 * ### Two tables, not a tree
 *
 * A consumer with no keys must still be visible — it is the thing you issue a
 * key *to* — and a key is meaningless without the consumer it belongs to. So
 * the consumers list is one `<DataTable>` and the keys are a second, flattened
 * across consumers with the owner as a column. Both get the mandatory
 * skeleton / empty / error states from the same query, and neither hides a row
 * behind an expander.
 *
 * ### Refusals are shown, never hidden
 *
 * Every mutating control is disabled *with the reason on it*
 * (`useCannotBecause`), per `plan-preproduction` R3 — the server's
 * `requireCapability('platform:manage')` is still the gate, and an analyst
 * reading this screen learns whose job issuance is rather than meeting a 403.
 */

/** The three `/v1` scopes, in the order the contract enumerates them. */
const SCOPES: readonly Scope[] = ['assessments:write', 'results:read', 'reference:read'];

const scopeHint = (scope: Scope): StringKey => `settings.scope.${scope}` as StringKey;

/** One key, plus the consumer it belongs to — the flattened row of table two. */
interface OwnedKey extends ApiKeyRow {
  consumerId: string;
  consumerName: string;
}

/* ------------------------------------------------------------------------ */
/*  The once-only panel, shared by issue and rotate                          */
/* ------------------------------------------------------------------------ */

/**
 * The plaintext, and the sentence saying it will not be shown again.
 *
 * `notice` is a LOCALISED key rather than the server's `notice` field: the
 * server sends English (correctly — `/v1` is English on the wire, `33b`), and
 * the operator reading this screen may not be reading English.
 */
function IssuedKeyPanel({
  issued, title, notice, onDone,
}: {
  issued: IssuedKey;
  title: string;
  notice: string;
  onDone: () => void;
}) {
  const t = useT();

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
        <DialogDescription>{notice}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-2">
        <Label htmlFor="issued-key">{issued.label}</Label>
        <div className="flex gap-2">
          {/* The key is an opaque ASCII credential: `dir="ltr"` so it never
              reorders inside an Arabic console, and monospaced so a transcribed
              character is checkable. */}
          <Input
            id="issued-key"
            readOnly
            value={issued.apiKey}
            className="font-mono"
            dir="ltr"
            aria-label={t('settings.column.key')}
          />
          <Button
            variant="outline"
            aria-label={t('settings.keys.copyKey')}
            title={t('settings.keys.copyKey')}
            onClick={() => {
              void navigator.clipboard?.writeText(issued.apiKey);
              notify.success(t('common.copied'));
            }}
          >
            <CopyIcon className="size-4" />
          </Button>
        </div>
        <div className="flex flex-wrap gap-1">
          {issued.scopes.map((scope) => (
            <Badge key={scope} variant="outline" className="font-mono" dir="ltr">{scope}</Badge>
          ))}
        </div>
      </div>

      <DialogFooter>
        <Button onClick={onDone}>{t('common.done')}</Button>
      </DialogFooter>
    </DialogContent>
  );
}

/* ------------------------------------------------------------------------ */
/*  Register a consumer                                                      */
/* ------------------------------------------------------------------------ */

function RegisterConsumerDialog({ onDone }: { onDone: () => void }) {
  const t = useT();
  const [name, setName] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [webhookSecret, setWebhookSecret] = useState('');
  const [fields, setFields] = useState<{ name?: string; secret?: string }>({});
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () => {
      const url = webhookUrl.trim();
      return api.createConsumer({
        name: name.trim(),
        ...(url ? { webhookUrl: url, webhookSecret } : {}),
      });
    },
    onSuccess: (consumer) => {
      notify.success(t('settings.consumers.created'), consumer.name);
      onDone();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : t('settings.consumers.createFailed')),
  });

  /**
   * THE PAIRING RULE IS CHECKED HERE TOO, and nothing is sent when it fails.
   *
   * `ConsumerCreate` refines it server-side and answers 422, which is the
   * enforcement — but a 422 is a poor way to learn a rule the form could have
   * stated, and the message would land on the dialog as a wall rather than
   * against the field that is wrong. So this refuses locally, against the
   * `webhookSecret` field, and the request is never made.
   */
  const submit = () => {
    const url = webhookUrl.trim();
    const next: { name?: string; secret?: string } = {};

    if (!name.trim()) next.name = t('settings.consumers.nameRequired');
    if (url && !webhookSecret) next.secret = t('settings.consumers.secretRequired');
    else if (url && webhookSecret.length < 16) next.secret = t('settings.consumers.secretTooShort');

    setFields(next);
    if (next.name || next.secret) return;

    setError(null);
    create.mutate();
  };

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('settings.consumers.new.title')}</DialogTitle>
        <DialogDescription>{t('settings.consumers.new.description')}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="consumer-name">{t('settings.consumers.name')}</Label>
          <Input
            id="consumer-name"
            value={name}
            aria-invalid={Boolean(fields.name)}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('settings.consumers.namePlaceholder')}
          />
          {fields.name && (
            <p role="alert" className="text-destructive text-sm">{fields.name}</p>
          )}
        </div>

        <div className="grid gap-2">
          <Label htmlFor="consumer-webhook">{t('settings.consumers.webhookUrl')}</Label>
          <Input
            id="consumer-webhook"
            type="url"
            value={webhookUrl}
            dir="ltr"
            className="font-mono"
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://example.gov.ae/hooks/nafis"
          />
        </div>

        <div className="grid gap-2">
          <Label htmlFor="consumer-secret">{t('settings.consumers.webhookSecret')}</Label>
          <Input
            id="consumer-secret"
            type="password"
            value={webhookSecret}
            dir="ltr"
            aria-invalid={Boolean(fields.secret)}
            onChange={(e) => setWebhookSecret(e.target.value)}
          />
          {fields.secret ? (
            <p role="alert" className="text-destructive text-sm">{fields.secret}</p>
          ) : (
            <p className="text-muted-foreground text-xs">
              {t('settings.consumers.webhookSecret.hint')}
            </p>
          )}
        </div>

        {/* A server refusal belongs INSIDE the dialog, next to the fields that
            caused it — not in a toast behind it (10 §6). */}
        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <DialogFooter>
        <Button onClick={submit} disabled={create.isPending}>
          {create.isPending ? t('settings.consumers.creating') : t('settings.consumers.create')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/* ------------------------------------------------------------------------ */
/*  Issue a key                                                              */
/* ------------------------------------------------------------------------ */

function IssueKeyDialog({
  consumer, onIssued, onClose,
}: {
  consumer: ConsumerRow;
  onIssued: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const [label, setLabel] = useState('');
  const [scopes, setScopes] = useState<Scope[]>([]);
  const [fields, setFields] = useState<{ label?: string; scopes?: string }>({});
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  const issue = useMutation({
    mutationFn: () => api.issueKey(consumer.consumerId, { label: label.trim(), scopes }),
    onSuccess: (key) => {
      setError(null);
      setIssued(key);
      // Refresh the lists BEHIND the dialog: the new key's row should already be
      // there when the operator closes it.
      onIssued();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : t('settings.keys.issueFailed')),
  });

  const toggle = (scope: Scope, on: boolean) =>
    setScopes((current) => (on ? [...current, scope] : current.filter((s) => s !== scope)));

  const submit = () => {
    const next: { label?: string; scopes?: string } = {};
    if (!label.trim()) next.label = t('settings.keys.labelHint');
    // A key with no scope authenticates and can then call nothing, which reads
    // as a broken credential rather than as an empty one. The server refuses it
    // too (`scopes` is `.min(1)`); this says so before the round trip.
    if (scopes.length === 0) next.scopes = t('settings.keys.scopeRequired');

    setFields(next);
    if (next.label || next.scopes) return;

    setError(null);
    issue.mutate();
  };

  if (issued) {
    return (
      <IssuedKeyPanel
        issued={issued}
        title={t('settings.keys.issued.title')}
        notice={t('settings.keys.issued.notice')}
        onDone={onClose}
      />
    );
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('settings.keys.issue.title', { consumer: consumer.name })}</DialogTitle>
        <DialogDescription>{t('settings.keys.issue.description')}</DialogDescription>
      </DialogHeader>

      <div className="grid gap-4">
        <div className="grid gap-2">
          <Label htmlFor="key-label">{t('settings.keys.label')}</Label>
          <Input
            id="key-label"
            value={label}
            aria-invalid={Boolean(fields.label)}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('settings.keys.labelPlaceholder')}
          />
          <p
            className={fields.label ? 'text-destructive text-sm' : 'text-muted-foreground text-xs'}
            {...(fields.label ? { role: 'alert' as const } : {})}
          >
            {t('settings.keys.labelHint')}
          </p>
        </div>

        <fieldset className="grid gap-2">
          <legend className="text-sm font-medium">{t('settings.keys.scopes.legend')}</legend>
          {SCOPES.map((scope) => (
            <div key={scope} className="flex items-start gap-2">
              <Checkbox
                id={`scope-${scope}`}
                className="mt-0.5"
                checked={scopes.includes(scope)}
                onCheckedChange={(on) => toggle(scope, on === true)}
              />
              <Label htmlFor={`scope-${scope}`} className="grid gap-0.5 font-normal">
                {/* The scope STRING stays English and monospaced — it is what an
                    integrator pastes into their config (33b). The sentence
                    beside it is the console's, and is translated. */}
                <span className="font-mono text-sm" dir="ltr">{scope}</span>
                <span className="text-muted-foreground text-xs">{t(scopeHint(scope))}</span>
              </Label>
            </div>
          ))}
          {fields.scopes && (
            <p role="alert" className="text-destructive text-sm">{fields.scopes}</p>
          )}
        </fieldset>

        {error && (
          <Alert variant="destructive" role="alert">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}
      </div>

      <DialogFooter>
        <Button onClick={submit} disabled={issue.isPending}>
          {issue.isPending ? t('settings.keys.issuing') : t('settings.issueKey')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/* ------------------------------------------------------------------------ */
/*  Rotate                                                                   */
/* ------------------------------------------------------------------------ */

function RotateKeyDialog({
  target, onRotated, onClose,
}: {
  target: OwnedKey;
  onRotated: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const [error, setError] = useState<string | null>(null);
  const [issued, setIssued] = useState<IssuedKey | null>(null);

  const rotate = useMutation({
    mutationFn: () => api.rotateKey(target.consumerId, target.keyId),
    onSuccess: (key) => {
      setError(null);
      setIssued(key);
      onRotated();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : t('settings.keys.rotateFailed')),
  });

  if (issued) {
    return (
      <IssuedKeyPanel
        issued={issued}
        title={t('settings.keys.rotated.title')}
        notice={t('settings.keys.rotated.notice')}
        onDone={onClose}
      />
    );
  }

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('settings.keys.rotate.title', { label: target.label })}</DialogTitle>
        <DialogDescription>{t('settings.keys.rotate.description')}</DialogDescription>
      </DialogHeader>

      {error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
        <Button onClick={() => rotate.mutate()} disabled={rotate.isPending}>
          {rotate.isPending ? t('settings.keys.rotating') : t('settings.keys.rotate')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/* ------------------------------------------------------------------------ */
/*  Revoke                                                                   */
/* ------------------------------------------------------------------------ */

function RevokeKeyDialog({
  target, onRevoked, onClose,
}: {
  target: OwnedKey;
  onRevoked: () => void;
  onClose: () => void;
}) {
  const t = useT();
  const [error, setError] = useState<string | null>(null);

  const revoke = useMutation({
    mutationFn: () => api.revokeKey(target.consumerId, target.keyId),
    onSuccess: () => {
      notify.success(t('settings.keys.revoked'), target.label);
      onRevoked();
      onClose();
    },
    onError: (err) =>
      setError(err instanceof ApiError ? err.message : t('settings.keys.revokeFailed')),
  });

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{t('settings.keys.revoke.title', { label: target.label })}</DialogTitle>
        <DialogDescription>{t('settings.keys.revoke.description')}</DialogDescription>
      </DialogHeader>

      {/* Said in the dialog, not only in the docs: `DELETE` on the route is
          `active:false` in the document, and an operator expecting a deletion
          should find out here rather than from the row that stayed. */}
      <p className="text-muted-foreground text-sm">{t('settings.keys.revoke.recordKept')}</p>

      {error && (
        <Alert variant="destructive" role="alert">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="destructive" onClick={() => revoke.mutate()} disabled={revoke.isPending}>
          {revoke.isPending ? t('settings.keys.revoking') : t('settings.keys.revoke')}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

/* ------------------------------------------------------------------------ */
/*  The screen                                                               */
/* ------------------------------------------------------------------------ */

export function ApiKeys() {
  const t = useT();
  const dateTime = useDateTime();
  const queryClient = useQueryClient();

  const consumers = useQuery({ queryKey: ['consumers'], queryFn: api.consumers });
  const cannotManage = useCannotBecause('platform:manage');

  const [registering, setRegistering] = useState(false);
  const [issueFor, setIssueFor] = useState<ConsumerRow | null>(null);
  const [rotateFor, setRotateFor] = useState<OwnedKey | null>(null);
  const [revokeFor, setRevokeFor] = useState<OwnedKey | null>(null);

  const invalidate = () => void queryClient.invalidateQueries({ queryKey: ['consumers'] });

  const setActive = useMutation({
    mutationFn: ({ consumerId, active }: { consumerId: string; active: boolean }) =>
      api.updateConsumer(consumerId, { active }),
    onSuccess: (consumer) => {
      notify.success(
        t('settings.consumers.updated'),
        t(consumer.active
          ? 'settings.consumers.reactivated.detail'
          : 'settings.consumers.deactivated.detail'),
      );
      invalidate();
    },
    onError: (err) =>
      notify.error(
        t('settings.consumers.updateFailed'),
        err instanceof ApiError ? err.message : t('table.errorDescription'),
      ),
  });

  const rows = consumers.data?.items ?? [];

  /** Every key, with its owner attached. A key without its consumer is unusable. */
  const keys = useMemo<OwnedKey[]>(
    () => rows.flatMap((consumer) =>
      consumer.apiKeys.map((key) => ({
        ...key,
        consumerId: consumer.consumerId,
        consumerName: consumer.name,
      }))),
    [rows],
  );

  const consumerColumns = useMemo<ColumnDef<ConsumerRow, unknown>[]>(
    () => [
      {
        header: t('settings.consumers.column.consumer'),
        accessorKey: 'name',
        cell: ({ row }) => (
          <div>
            <p className="font-medium">{row.original.name}</p>
            <p className="text-muted-foreground font-mono text-xs" dir="ltr">
              {row.original.consumerId}
            </p>
          </div>
        ),
      },
      {
        header: t('questions.column.status'),
        accessorKey: 'active',
        cell: ({ row }) => (
          <Badge variant={row.original.active ? 'default' : 'destructive'}>
            {t(row.original.active
              ? 'settings.consumers.status.active'
              : 'settings.consumers.status.inactive')}
          </Badge>
        ),
      },
      {
        header: t('settings.consumers.column.webhook'),
        accessorKey: 'webhookConfigured',
        cell: ({ row }) => (
          <div className="space-y-1">
            <Badge variant={row.original.webhookConfigured ? 'success' : 'outline'}>
              {t(row.original.webhookConfigured
                ? 'settings.consumers.webhookConfigured'
                : 'settings.consumers.webhookNone')}
            </Badge>
            {row.original.webhookUrl && (
              <p className="text-muted-foreground max-w-56 truncate font-mono text-xs" dir="ltr">
                {row.original.webhookUrl}
              </p>
            )}
          </div>
        ),
      },
      {
        header: t('settings.consumers.column.keys'),
        id: 'keys',
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {t('settings.consumers.keyCount', {
              active: row.original.apiKeys.filter((k) => k.active).length,
              total: row.original.apiKeys.length,
            })}
          </span>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              disabled={cannotManage !== null}
              title={cannotManage ?? undefined}
              onClick={() => setIssueFor(row.original)}
            >
              <KeyRoundIcon className="size-4" />
              {t('settings.issueKey')}
            </Button>
            <Button
              size="sm"
              variant="ghost"
              disabled={cannotManage !== null || setActive.isPending}
              title={cannotManage ?? undefined}
              onClick={() =>
                setActive.mutate({
                  consumerId: row.original.consumerId,
                  active: !row.original.active,
                })}
            >
              {t(row.original.active
                ? 'settings.consumers.deactivate'
                : 'settings.consumers.reactivate')}
            </Button>
          </div>
        ),
      },
    ],
    [t, cannotManage, setActive],
  );

  const keyColumns = useMemo<ColumnDef<OwnedKey, unknown>[]>(
    () => [
      {
        header: t('settings.consumers.column.consumer'),
        accessorKey: 'consumerName',
        cell: ({ row }) => <span className="text-sm">{row.original.consumerName}</span>,
      },
      {
        header: t('settings.column.label'),
        accessorKey: 'label',
        cell: ({ row }) => (
          <div>
            <p className="font-medium">{row.original.label}</p>
            <p className="text-muted-foreground font-mono text-xs" dir="ltr">
              {row.original.keyId}
            </p>
          </div>
        ),
      },
      {
        header: t('settings.column.scopes'),
        accessorKey: 'scopes',
        cell: ({ row }) => (
          <div className="flex flex-wrap gap-1">
            {row.original.scopes.map((scope) => (
              <Badge key={scope} variant="outline" className="font-mono" dir="ltr">{scope}</Badge>
            ))}
          </div>
        ),
      },
      {
        header: t('settings.column.created'),
        accessorKey: 'createdAt',
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {row.original.createdAt ? dateTime(row.original.createdAt) : t('settings.keys.never')}
          </span>
        ),
      },
      {
        header: t('settings.keys.column.rotated'),
        accessorKey: 'rotatedAt',
        cell: ({ row }) => (
          <span className="text-muted-foreground text-sm">
            {row.original.rotatedAt ? dateTime(row.original.rotatedAt) : t('settings.keys.never')}
          </span>
        ),
      },
      {
        header: t('questions.column.status'),
        accessorKey: 'active',
        cell: ({ row }) => (
          <Badge variant={row.original.active ? 'default' : 'destructive'}>
            {t(row.original.active ? 'settings.keys.status.active' : 'settings.keys.status.revoked')}
          </Badge>
        ),
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={cannotManage !== null}
              title={cannotManage ?? undefined}
              onClick={() => setRotateFor(row.original)}
            >
              {t('settings.keys.rotate')}
            </Button>
            {/* A revoked key keeps its row — there is nothing left to revoke, so
                the control goes rather than sitting there doing nothing. */}
            {row.original.active && (
              <Button
                size="sm"
                variant="ghost"
                disabled={cannotManage !== null}
                title={cannotManage ?? undefined}
                onClick={() => setRevokeFor(row.original)}
              >
                {t('settings.keys.revoke')}
              </Button>
            )}
          </div>
        ),
      },
    ],
    [t, dateTime, cannotManage],
  );

  return (
    <div className="space-y-8">
      <section className="space-y-4">
        <div className="flex items-start justify-between gap-2">
          {/* The card wrapping this screen already carries
              `settings.apiKeys.description`; repeating it here would be the
              same sentence twice on one tab. */}
          <h3 className="font-medium">{t('settings.consumers.title')}</h3>
          <Dialog open={registering} onOpenChange={setRegistering}>
            <DialogTrigger asChild>
              <Button size="sm" disabled={cannotManage !== null} title={cannotManage ?? undefined}>
                <PlusIcon className="size-4" />
                {t('settings.consumers.register')}
              </Button>
            </DialogTrigger>
            <RegisterConsumerDialog
              onDone={() => {
                setRegistering(false);
                invalidate();
              }}
            />
          </Dialog>
        </div>

        <DataTable
          columns={consumerColumns}
          rows={rows}
          isLoading={consumers.isLoading}
          error={consumers.error}
          onRetry={() => void consumers.refetch()}
          emptyTitle={t('settings.consumers.empty.title')}
          emptyDescription={t('settings.consumers.empty.description')}
        />
      </section>

      <section className="space-y-4">
        <h3 className="font-medium">{t('settings.keys.issuedKeys')}</h3>
        <DataTable
          columns={keyColumns}
          rows={keys}
          isLoading={consumers.isLoading}
          error={consumers.error}
          onRetry={() => void consumers.refetch()}
          emptyTitle={t('settings.apiKeys.empty.title')}
          emptyDescription={t('settings.apiKeys.empty.description')}
        />
      </section>

      {/* Controlled and mounted only while open, so each one starts clean — and
          so a plaintext key cannot survive in a closed dialog's state. */}
      <Dialog open={issueFor !== null} onOpenChange={(open) => !open && setIssueFor(null)}>
        {issueFor && (
          <IssueKeyDialog
            consumer={issueFor}
            onIssued={invalidate}
            onClose={() => setIssueFor(null)}
          />
        )}
      </Dialog>

      <Dialog open={rotateFor !== null} onOpenChange={(open) => !open && setRotateFor(null)}>
        {rotateFor && (
          <RotateKeyDialog
            target={rotateFor}
            onRotated={invalidate}
            onClose={() => setRotateFor(null)}
          />
        )}
      </Dialog>

      <Dialog open={revokeFor !== null} onOpenChange={(open) => !open && setRevokeFor(null)}>
        {revokeFor && (
          <RevokeKeyDialog
            target={revokeFor}
            onRevoked={invalidate}
            onClose={() => setRevokeFor(null)}
          />
        )}
      </Dialog>
    </div>
  );
}
